import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { isEmpty, omit } from "lodash";

import {
  globalSearchQuery,
  searchHiddenGroupsQuery,
} from "src/queries/globalSearch.graphql";
import { MEMBERSHIP_EVENTS } from "src/components/Person/events";
import * as searchUtil from "src/util/search";
import { reportError } from "src/services/errorReporting";
import eventEmitter from "src/services/eventEmitter";
import { roleBasedTagTypeIds } from "src/util/tags";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { trackEvent } from "src/services/segment";
import { trackSearchResultsViewedOnce } from "src/services/exploreOrganisationAnalytics";

/**
 * useGlobalSearch - Standalone hook for global search functionality
 *
 * Handles query execution, result formatting, and pagination.
 * Relies on Apollo caching for cross-component result sharing.
 *
 * @param {import("./types").GlobalSearchOptions} options
 */
export const useGlobalSearch = (options = {}) => {
  const defaultResultsViewedScope = useId();
  const {
    searchTerm,
    size,
    scope = null,
    peopleFilters = [],
    dataFilters = {},
    groupFilters = [],
    defaultTeamTypes = [],
    positionFilters = [],
    positionOccupancyStatus = null,
    tagTypesConfig = {},
    tagsVisibleIn,
    includePeople = true,
    includeGroups = true,
    includeHiddenGroups = false,
    includeTags = true,
    includePositions = false,
    includePositionFields = false,
    customer = null,
    disabled = false,
    allowEmptySearch = false,
    resultsViewedScope = defaultResultsViewedScope,
  } = options;

  const client = useApolloClient();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [results, setResults] = useState(null);
  const [queryVars, setQueryVars] = useState(null);
  const [paginationVars, setPaginationVars] = useState(null);
  const inFlightRequestKeyRef = useRef(null);
  // Survives cache-first replays of the same globalSearch variables after allocate.
  const personPatchesRef = useRef(new Map());

  // Only the teams section can opt into hidden teams, and it uses its own query.
  const wantsHiddenGroups = includeGroups && includeHiddenGroups;

  const executeSearch = useCallback(async () => {
    if (
      disabled ||
      (!includePeople && !includeGroups && !includeTags && !includePositions)
    ) {
      return;
    }

    const thisRequestKey = JSON.stringify(queryVars);
    inFlightRequestKeyRef.current = thisRequestKey;

    try {
      setError(null);
      setLoading(true);

      const hiddenGroupsSearch = Boolean(queryVars?.includeHiddenGroups);
      const searchQuery = hiddenGroupsSearch
        ? searchHiddenGroupsQuery
        : globalSearchQuery;

      // cache-first serves the store when present; otherwise it hits the network.
      // Sticky MEMBERSHIP_CHANGED overlays must only paper over cache hits — a
      // network payload is authoritative and should retire those patches.
      let servedFromCache = false;
      try {
        servedFromCache =
          client.readQuery({
            query: searchQuery,
            variables: queryVars,
          }) != null;
      } catch {
        servedFromCache = false;
      }

      const { data } = await client.query({
        query: searchQuery,
        variables: queryVars,
        fetchPolicy: "cache-first",
      });

      // listGroups returns pagedGroups at the root; nest it so the shared
      // formatter and pagination merge see the shape globalSearch returns.
      const searchResult = hiddenGroupsSearch ? { result: data } : data;

      // Ignore response if search term/vars changed while this request was in flight
      if (inFlightRequestKeyRef.current !== thisRequestKey) {
        return;
      }

      searchUtil.prunePersonPatchesAgainstSearchData(
        personPatchesRef.current,
        searchResult,
        { trustIncoming: !servedFromCache }
      );

      setSearchData((prevData) =>
        searchUtil.mergeIncomingGlobalSearchResult({
          prevData,
          searchResult,
          patches: personPatchesRef.current,
        })
      );
    } catch (err) {
      if (inFlightRequestKeyRef.current !== thisRequestKey) {
        return;
      }
      reportError(err);
      setError(err);
    }
  }, [
    client,
    disabled,
    includeGroups,
    includePeople,
    includePositions,
    includeTags,
    queryVars,
  ]);

  // Update query vars
  useEffect(() => {
    const roleBasedTagTypes = roleBasedTagTypeIds(
      tagTypesConfig,
      tagsVisibleIn
    );

    const selectedTeamFilters = dataFilters.teams || {};
    const teamFilters = !isEmpty(scope)
      ? {
          teams: {
            ...selectedTeamFilters,
            teams: [scope?.id],
            includeSubTeams: true,
            intersectingTeamMembers: true,
            types: [
              ...new Set([
                ...(selectedTeamFilters.types || []),
                ...(defaultTeamTypes || []),
              ]),
            ],
          },
        }
      : {
          teams: {
            ...selectedTeamFilters,
            types: [
              ...new Set([
                ...(selectedTeamFilters.types || []),
                ...(defaultTeamTypes || []),
              ]),
            ],
          },
        };

    const tagFilters = includeTags
      ? { tags: { tagTypes: roleBasedTagTypes } }
      : {};

    const searchTerms = searchTerm ? [searchTerm] : [];

    // listGroups takes the scope as ids + types rather than an InsightFilter.
    const newVars = wantsHiddenGroups
      ? {
          size,
          searchTerms,
          includeHiddenGroups: true,
          includeEmptyGroups: true,
          // An empty list means "no type filter"; sending [] matches no teams.
          groupTypes: isEmpty(defaultTeamTypes) ? null : defaultTeamTypes,
          groupIds: isEmpty(scope) ? null : [scope?.id],
          includeDescendants: !isEmpty(scope),
          ...paginationVars,
        }
      : {
          size,
          searchTerms,
          includePeople,
          includeGroups,
          includeTags,
          includePositions,
          includePositionFields,
          includeEmptyGroups: true,
          personFieldsToSearch: includePeople ? peopleFilters : [],
          positionFieldsToSearch: includePositions ? positionFilters : [],
          positionOccupancyStatus:
            includePositions && positionOccupancyStatus
              ? [positionOccupancyStatus]
              : null,
          groupFieldsToSearch: includeGroups ? groupFilters : [],
          tagFieldsToSearch: ["value", "type"],
          filter: {
            ...dataFilters,
            ...teamFilters,
            ...tagFilters,
          },
          ...paginationVars,
        };

    setQueryVars((prevVars) => {
      // If no change in query, skip
      if (JSON.stringify(newVars) === JSON.stringify(prevVars)) {
        return prevVars;
      }

      // If non-pagination vars changed, wipe state
      const omitFields = [
        "personSearchAfterId",
        "groupsOffset",
        "tagsOffset",
        "positionsPage",
      ];
      if (
        JSON.stringify(omit(newVars, omitFields)) !==
        JSON.stringify(omit(prevVars, omitFields))
      ) {
        setSearchData(null);
        setResults(null);
        setPaginationVars(null);

        // if we actually changed the pagination vars, then we will be regenerating these query vars immediately anyway, so don't return the new vars now
        if (paginationVars !== null) {
          return prevVars;
        }
      }

      return newVars;
    });
  }, [
    size,
    searchTerm,
    includePeople,
    includeGroups,
    wantsHiddenGroups,
    includeTags,
    includePositions,
    includePositionFields,
    peopleFilters,
    dataFilters,
    positionFilters,
    positionOccupancyStatus,
    groupFilters,
    defaultTeamTypes,
    scope,
    tagTypesConfig,
    tagsVisibleIn,
    paginationVars,
  ]);

  // Exec search on query vars change or when disabled becomes false (e.g. navigating from search page)
  // When !allowEmptySearch, skip empty to avoid extra search before debounce settles (header search)
  useEffect(() => {
    const haveTerm = queryVars?.searchTerms?.length > 0;
    if (queryVars && !disabled && (allowEmptySearch || haveTerm)) {
      executeSearch();
    }
  }, [queryVars, disabled, executeSearch, allowEmptySearch]);

  // Allocations modal (and other membership editors) emit MEMBERSHIP_CHANGED with
  // the refreshed person. Patch local people results (and remember the patch so
  // cache-first replays of the same variables do not revive pre-allocation data)
  // so team chips and the client-side allocationStatus filter stay correct.
  useEffect(() => {
    if (!includePeople || disabled) {
      return undefined;
    }

    const handleMembershipChanged = (payload) => {
      const updatedPerson = payload?.person;
      if (!updatedPerson?.aggregateId) {
        return;
      }

      const hasMembershipFields =
        updatedPerson.allocations !== undefined ||
        updatedPerson.memberOf !== undefined;

      // Sticky overlay is only for membership fields — incomplete payloads must
      // not freeze later network search rows.
      if (hasMembershipFields) {
        personPatchesRef.current.set(updatedPerson.aggregateId, updatedPerson);
      }

      // Keep normalized Person entities in sync so remounts / other readers
      // that resolve Person:${id} also see allocations + memberOf. Only write
      // fields the payload actually includes — never invent [] via ??.
      const fields = {};
      if (updatedPerson.allocations !== undefined) {
        fields.allocations = () => updatedPerson.allocations;
      }
      if (updatedPerson.memberOf !== undefined) {
        fields.memberOf = () => updatedPerson.memberOf;
      }
      if (Object.keys(fields).length > 0) {
        try {
          client.cache.modify({
            id: `Person:${updatedPerson.aggregateId}`,
            fields,
          });
        } catch {
          // Person may not be in the cache yet; local patch still covers this page.
        }
      }

      setSearchData((prev) =>
        searchUtil.patchPersonInGlobalSearchData(prev, updatedPerson)
      );
    };

    eventEmitter.on(
      MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED,
      handleMembershipChanged
    );

    return () => {
      eventEmitter.off(
        MEMBERSHIP_EVENTS.MEMBERSHIP_CHANGED,
        handleMembershipChanged
      );
    };
  }, [client, disabled, includePeople]);

  // Updates pagination vars
  const onLoadMore = useCallback(() => {
    if (!searchData || loading) {
      return;
    }

    setPaginationVars((prevVars) => {
      const lastPersonId = searchData?.result?.pagedPeople?.searchAfterId;
      const groupCount = searchData?.result?.pagedGroups?.groups?.length;
      const tagCount = searchData?.result?.pagedTags?.tags?.length;
      const positionsCount =
        searchData?.result?.pagedPositions?.positions?.length ?? 0;
      const nextPositionsPage =
        includePositions && size > 0
          ? Math.floor(positionsCount / size) + 1
          : undefined;

      const newVars = {
        ...(includePeople && lastPersonId
          ? { personSearchAfterId: lastPersonId }
          : {}),
        ...(includeGroups && groupCount ? { groupsOffset: groupCount } : {}),
        ...(includeTags && tagCount ? { tagsOffset: tagCount } : {}),
        ...(includePositions && nextPositionsPage > 1
          ? { positionsPage: nextPositionsPage }
          : {}),
      };

      if (JSON.stringify(newVars) === JSON.stringify(prevVars)) {
        return prevVars;
      }

      return newVars;
    });
  }, [
    includeGroups,
    includePeople,
    includePositions,
    includeTags,
    loading,
    searchData,
    size,
  ]);

  // Formats search data
  useEffect(() => {
    if (disabled) {
      setLoading(false);
      setResults(null);
      setSearchData(null);
      return;
    }

    if (!searchData) {
      setResults(null);
      return;
    }

    setLoading(false);

    const formatted = searchUtil.getFormattedSearchResults({
      data: searchData,
      searchTerm,
      customer,
      showHiddenGroups: wantsHiddenGroups,
    });

    setResults((prev) => {
      // if same result, skip
      if (JSON.stringify(formatted) === JSON.stringify(prev)) {
        return prev;
      }

      const newGroupsCount =
        searchData?.result?.pagedGroups?.groups?.length ?? 0;
      const newTagsCount = searchData?.result?.pagedTags?.tags?.length ?? 0;
      const newPositionsCount =
        searchData?.result?.pagedPositions?.positions?.length ?? 0;
      const pageForTracking = Math.max(
        includeGroups ? Math.ceil(newGroupsCount / size) : 1,
        includeTags ? Math.ceil(newTagsCount / size) : 1,
        includePositions ? Math.ceil(newPositionsCount / size) : 1,
        1
      );
      // LEGACY until 2026-11-17 — effect-scoped; leave as-is during dual-emit.
      trackEvent(EVENT_TRACKING.SEARCH_VIEWED, {
        pagination: pageForTracking,
      });
      // Mount-scoped across parallel people/groups/tags/positions instances.
      trackSearchResultsViewedOnce(resultsViewedScope, searchTerm, {
        pagination: pageForTracking,
      });

      return formatted;
    });
  }, [
    searchData,
    searchTerm,
    customer,
    size,
    includeGroups,
    includeTags,
    includePositions,
    disabled,
    resultsViewedScope,
    wantsHiddenGroups,
  ]);

  return {
    // State
    loading,
    error,

    // Results ready for rendering
    results,

    // Actions
    onLoadMore,
  };
};

export default useGlobalSearch;
